import React, { useState, useEffect } from 'react';
import { X, Bell, Save, Plus, Trash2 } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { useNotification } from '@/contexts/NotificationContext';
import { doc, getDoc, updateDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '@/firebase/config';

interface VendorContractRenewalPreferences {
  enabled: boolean;
  frequency: 'daily' | 'weekly' | 'monthly';
  day: number; // 0-6 for weekly (0=Sunday), 1-31 for monthly
  time: string; // HH:MM format in MST
  daysBeforeExpiry: number[]; // Array of days before expiry (e.g., [30, 14, 7])
}

interface VendorContractRenewalSettingsModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const VendorContractRenewalSettingsModal: React.FC<VendorContractRenewalSettingsModalProps> = ({ isOpen, onClose }) => {
  const { user } = useAuth();
  const { showNotification } = useNotification();
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [newDaysBeforeExpiry, setNewDaysBeforeExpiry] = useState('');
  const [preferences, setPreferences] = useState<VendorContractRenewalPreferences>({
    enabled: true,
    frequency: 'weekly',
    day: 1, // Monday
    time: '08:00', // 8 AM MST
    daysBeforeExpiry: [30], // Default: 30 days
  });

  useEffect(() => {
    if (isOpen && user?.id) {
      loadPreferences();
    }
  }, [isOpen, user?.id]);

  const loadPreferences = async () => {
    if (!user?.id) return;

    setIsLoading(true);
    try {
      const userDoc = await getDoc(doc(db, 'users', user.id));
      if (userDoc.exists()) {
        const data = userDoc.data();
        const renewalPrefs = data.vendorContractRenewalNotifications as VendorContractRenewalPreferences | undefined;
        
        // Default values: weekly on Monday at 8 AM MST, 30 days before expiry
        const defaultPrefs: VendorContractRenewalPreferences = {
          enabled: true,
          frequency: 'weekly',
          day: 1, // Monday
          time: '08:00', // 8 AM MST
          daysBeforeExpiry: [30],
        };

        if (renewalPrefs) {
          // Handle backward compatibility: if daysBeforeExpiry is a number, convert to array
          let daysBeforeExpiry = renewalPrefs.daysBeforeExpiry;
          if (typeof daysBeforeExpiry === 'number') {
            daysBeforeExpiry = [daysBeforeExpiry];
          } else if (!Array.isArray(daysBeforeExpiry) || daysBeforeExpiry.length === 0) {
            daysBeforeExpiry = [30];
          }
          
          setPreferences({
            enabled: renewalPrefs.enabled ?? true,
            frequency: renewalPrefs.frequency || 'weekly',
            day: renewalPrefs.day ?? 1,
            time: renewalPrefs.time || '08:00',
            daysBeforeExpiry: daysBeforeExpiry,
          });
        } else {
          setPreferences(defaultPrefs);
        }
      } else {
        // User document doesn't exist, use defaults
        setPreferences({
          enabled: true,
          frequency: 'weekly',
          day: 1,
          time: '08:00',
          daysBeforeExpiry: [30],
        });
      }
    } catch (error) {
      console.error('Error loading preferences:', error);
      showNotification('Failed to load preferences', 'error');
    } finally {
      setIsLoading(false);
    }
  };

  const savePreferences = async () => {
    if (!user?.id) {
      showNotification('You must be logged in to save preferences', 'error');
      return;
    }

    if (preferences.daysBeforeExpiry.length === 0) {
      showNotification('Please add at least one days before expiry entry', 'error');
      return;
    }

    setIsSaving(true);
    try {
      const userRef = doc(db, 'users', user.id);
      await updateDoc(userRef, {
        vendorContractRenewalNotifications: preferences,
        updatedAt: serverTimestamp(),
      });

      showNotification('Vendor contract renewal preferences saved successfully!', 'success');
      onClose();
    } catch (error) {
      console.error('Error saving preferences:', error);
      showNotification('Failed to save preferences', 'error');
    } finally {
      setIsSaving(false);
    }
  };

  const addDaysBeforeExpiry = () => {
    const days = parseInt(newDaysBeforeExpiry.trim(), 10);
    
    if (isNaN(days) || days < 1 || days > 365) {
      showNotification('Please enter a valid number between 1 and 365', 'error');
      return;
    }

    if (preferences.daysBeforeExpiry.includes(days)) {
      showNotification('This number is already in the list', 'error');
      return;
    }

    setPreferences({
      ...preferences,
      daysBeforeExpiry: [...preferences.daysBeforeExpiry, days].sort((a, b) => b - a), // Sort descending
    });
    setNewDaysBeforeExpiry('');
  };

  const removeDaysBeforeExpiry = (daysToRemove: number) => {
    setPreferences({
      ...preferences,
      daysBeforeExpiry: preferences.daysBeforeExpiry.filter(days => days !== daysToRemove),
    });
  };

  const weekDays = [
    { value: 0, label: 'Sunday' },
    { value: 1, label: 'Monday' },
    { value: 2, label: 'Tuesday' },
    { value: 3, label: 'Wednesday' },
    { value: 4, label: 'Thursday' },
    { value: 5, label: 'Friday' },
    { value: 6, label: 'Saturday' },
  ];

  const monthDays = Array.from({ length: 31 }, (_, i) => i + 1);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto m-4">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b">
          <div className="flex items-center gap-2">
            <Bell className="w-5 h-5 text-purple-600" />
            <h2 className="text-lg font-semibold text-gray-900">Vendor Contract Renewal Notifications</h2>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <div className="p-4 space-y-4">
          {isLoading ? (
            <div className="flex items-center justify-center py-8">
              <div className="text-center">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600 mx-auto mb-2"></div>
                <p className="text-sm text-gray-600">Loading preferences...</p>
              </div>
            </div>
          ) : (
            <>
              {/* Enable/Disable Toggle */}
              <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                <div>
                  <label className="text-sm font-medium text-gray-900">
                    Enable Vendor Contract Renewal Notifications
                  </label>
                  <p className="text-xs text-gray-500 mt-0.5">
                    Receive email alerts for vendor contracts expiring soon
                  </p>
                </div>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={preferences.enabled}
                    onChange={(e) =>
                      setPreferences({ ...preferences, enabled: e.target.checked })
                    }
                    className="sr-only peer"
                  />
                  <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-purple-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-purple-600"></div>
                </label>
              </div>

              {preferences.enabled && (
                <>
                  {/* Days Before Expiry (Multiple) */}
                  <div>
                    <label className="block text-sm font-medium text-gray-900 mb-2">
                      Days Before Expiry to Notify <span className="text-red-600">*</span>
                    </label>
                    <p className="text-xs text-gray-500 mb-2">
                      Add multiple notification intervals (e.g., 30, 14, 7 days). You'll receive separate notifications at each interval.
                    </p>
                    <div className="space-y-2">
                      <div className="flex gap-2">
                        <input
                          type="number"
                          min="1"
                          max="365"
                          value={newDaysBeforeExpiry}
                          onChange={(e) => setNewDaysBeforeExpiry(e.target.value)}
                          onKeyPress={(e) => {
                            if (e.key === 'Enter') {
                              e.preventDefault();
                              addDaysBeforeExpiry();
                            }
                          }}
                          placeholder="Enter days (1-365)"
                          className="flex-1 px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                        />
                        <button
                          type="button"
                          onClick={addDaysBeforeExpiry}
                          className="px-3 py-2 text-sm bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors flex items-center gap-1"
                        >
                          <Plus className="w-4 h-4" />
                          Add
                        </button>
                      </div>
                      {preferences.daysBeforeExpiry.length > 0 && (
                        <div className="space-y-1">
                          {preferences.daysBeforeExpiry.map((days, index) => (
                            <div
                              key={index}
                              className="flex items-center justify-between p-2 bg-gray-50 rounded-lg border border-gray-200"
                            >
                              <div className="flex items-center gap-2">
                                <span className="text-sm font-medium text-gray-700">{days} day{days === 1 ? '' : 's'} before expiry</span>
                              </div>
                              <button
                                type="button"
                                onClick={() => removeDaysBeforeExpiry(days)}
                                className="text-red-600 hover:text-red-800 transition-colors"
                                title="Remove"
                              >
                                <Trash2 className="w-4 h-4" />
                              </button>
                            </div>
                          ))}
                        </div>
                      )}
                      {preferences.daysBeforeExpiry.length === 0 && (
                        <p className="text-xs text-gray-500 italic">No notification intervals added yet</p>
                      )}
                    </div>
                  </div>

                  {/* Frequency Selector */}
                  <div>
                    <label className="block text-sm font-medium text-gray-900 mb-2">
                      Notification Frequency
                    </label>
                    <div className="grid grid-cols-3 gap-2">
                      {(['daily', 'weekly', 'monthly'] as const).map((freq) => (
                        <button
                          key={freq}
                          type="button"
                          onClick={() => {
                            setPreferences({
                              ...preferences,
                              frequency: freq,
                              day: freq === 'weekly' ? 1 : freq === 'monthly' ? 1 : 0,
                            });
                          }}
                          className={`px-3 py-2 text-sm rounded-lg border-2 transition-all ${
                            preferences.frequency === freq
                              ? 'border-purple-600 bg-purple-50 text-purple-900 font-medium'
                              : 'border-gray-200 bg-white text-gray-700 hover:border-gray-300'
                          }`}
                        >
                          {freq.charAt(0).toUpperCase() + freq.slice(1)}
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Day/Date Selector */}
                  {preferences.frequency !== 'daily' && (
                    <div>
                      <label className="block text-sm font-medium text-gray-900 mb-2">
                        {preferences.frequency === 'weekly'
                          ? 'Day of Week'
                          : 'Day of Month'}
                      </label>
                      <select
                        value={preferences.day}
                        onChange={(e) =>
                          setPreferences({
                            ...preferences,
                            day: parseInt(e.target.value, 10),
                          })
                        }
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                      >
                        {preferences.frequency === 'weekly'
                          ? weekDays.map((day) => (
                              <option key={day.value} value={day.value}>
                                {day.label}
                              </option>
                            ))
                          : monthDays.map((day) => (
                              <option key={day} value={day}>
                                {day}
                                {day === 1 || day === 21 || day === 31
                                  ? 'st'
                                  : day === 2 || day === 22
                                  ? 'nd'
                                  : day === 3 || day === 23
                                  ? 'rd'
                                  : 'th'}
                              </option>
                            ))}
                      </select>
                    </div>
                  )}

                  {/* Time Selector */}
                  <div>
                    <label className="block text-sm font-medium text-gray-900 mb-2">
                      Time (MST)
                    </label>
                    <input
                      type="time"
                      value={preferences.time}
                      onChange={(e) =>
                        setPreferences({ ...preferences, time: e.target.value })
                      }
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      Notifications will be sent at this time in Mountain Standard Time (MST)
                    </p>
                  </div>

                  {/* Summary */}
                  <div className="p-3 bg-purple-50 rounded-lg border border-purple-200">
                    <p className="text-xs text-purple-900">
                      <strong>Summary:</strong> You will receive vendor contract renewal notifications{' '}
                      {preferences.frequency === 'daily'
                        ? 'every day'
                        : preferences.frequency === 'weekly'
                        ? `every ${weekDays.find((d) => d.value === preferences.day)?.label}`
                        : `on the ${preferences.day}${
                            preferences.day === 1 || preferences.day === 21 || preferences.day === 31
                              ? 'st'
                              : preferences.day === 2 || preferences.day === 22
                              ? 'nd'
                              : preferences.day === 3 || preferences.day === 23
                              ? 'rd'
                              : 'th'
                          } of each month`}{' '}
                      at {preferences.time} MST for contracts expiring in {preferences.daysBeforeExpiry.join(', ')} day{preferences.daysBeforeExpiry.length === 1 ? '' : 's'}.
                      <span className="block mt-1">
                        Notifications will be sent to your email address: {user?.email || 'Not set'}
                      </span>
                    </p>
                  </div>
                </>
              )}
            </>
          )}
        </div>

        {/* Footer */}
        <div className="flex justify-end gap-2 p-4 border-t bg-gray-50">
          <button
            onClick={onClose}
            className="px-4 py-1.5 text-sm text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={savePreferences}
            disabled={isSaving || isLoading}
            className="flex items-center gap-2 px-4 py-1.5 text-sm bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            {isSaving ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                <span>Saving...</span>
              </>
            ) : (
              <>
                <Save className="w-4 h-4" />
                <span>Save Preferences</span>
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default VendorContractRenewalSettingsModal;

